import React, { useState } from 'react';
import './contact-v7.css';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const INQUIRY_CHIPS = [
  'Accounting / Billing',
  'Career Hub / First Briefcase',
  'First Round',
  'General Inquiry',
  'Magazine',
  'Media Kit',
  'Membership',
  'Newsroom',
  'Round Table / Podcast',
  'Sponsorship',
];

const IDENTITY_OPTIONS = [
  '', 'Employer', 'Hiring Partner', 'Job Seeker',
  'Student / First Briefcase', 'Educator', 'Advertiser / Sponsor',
  'Government Partner', 'Subscriber / Reader', 'Other',
];

const MEDIA_TYPE_OPTIONS = [
  '', 'Interview Request', 'Comment or Statement Request',
  'Speaking Request', 'Press Coverage', 'Other',
];

const AD_INTERESTS = [
  'Website Banners', 'Homepage Sponsorship', 'Newsroom / Sector Sponsorship',
  'Magazine Advertising', 'Opportunity Unlocked Newsletter', 'First Round',
  'Round Table / Podcast', 'Video Advertising', 'Audio Advertising',
  'Sponsored Content', 'Career Hub / Featured Job', 'Custom Campaign', 'Other',
];

const SIGNALS = [
  { num: '1', text: 'Select the product, service, or business area that best matches your inquiry.' },
  { num: '2', text: 'Use the optional media and advertising fields only when they apply to you.' },
  { num: '3', text: 'One submission can include multiple advertising interests, formats, and placements.' },
];

const CONTACT_DETAILS = [
  { label: 'General Email',   value: 'Add when the page goes live' },
  { label: 'Phone',           value: 'Add when the page goes live' },
  { label: 'Mailing Address', value: 'Add when the page goes live' },
];

export default function ContactPage() {
  const [selectedInquiry, setSelectedInquiry] = useState('');
  const [firstName, setFirstName]   = useState('');
  const [lastName, setLastName]     = useState('');
  const [email, setEmail]           = useState('');
  const [phone, setPhone]           = useState('');
  const [organization, setOrg]      = useState('');
  const [identity, setIdentity]     = useState('');
  const [isMedia, setIsMedia]       = useState(false);
  const [mediaType, setMediaType]   = useState('');
  const [isAdv, setIsAdv]           = useState(false);
  const [adInterests, setAdInterests] = useState([]);
  const [message, setMessage]       = useState('');
  const [optIn, setOptIn]           = useState(false);
  const [loading, setLoading]       = useState(false);
  const [success, setSuccess]       = useState(false);

  const toggleAdInterest = (val) =>
    setAdInterests(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedInquiry) {
      document.getElementById('cv7-inquiry-chips')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setLoading(true);
    try {
      let fullMessage = message;
      if (isMedia && mediaType) fullMessage = `[Media Inquiry: ${mediaType}]\n\n${fullMessage}`;
      if (isAdv && adInterests.length > 0)
        fullMessage = `${fullMessage}\n\n[Advertising Interests: ${adInterests.join(', ')}]`;

      const res = await fetch(`${BACKEND}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inquiry_type: selectedInquiry,
          first_name: firstName,
          last_name: lastName,
          email,
          phone: phone || undefined,
          organization: organization || undefined,
          job_title: identity || undefined,
          subject: selectedInquiry,
          message: fullMessage,
          newsletter_opt_in: optIn,
          source_page: 'contact',
        }),
      });
      if (res.ok) {
        setSuccess(true);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      alert('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cv7-outer" data-testid="contact-page">
      <main className="cv7-page">
        <section className="cv7-shell" aria-label="Opportunity Post contact center">

          {/* ── Story Panel ─────────────────────────────────────────────── */}
          <aside className="cv7-story-panel">
            <div className="cv7-story-inner">
              <div className="cv7-brand">OPPORTUNITY POST</div>
              <div className="cv7-eyebrow">Contact Us</div>
              <h1 className="cv7-h1">We'd love to hear from you.</h1>
              <p className="cv7-lead">
                Whether you have a story idea, partnership opportunity, question, or need support, our team is here to help.
              </p>

              <div className="cv7-signal-list">
                {SIGNALS.map(({ num, text }) => (
                  <div key={num} className="cv7-signal">
                    <span className="cv7-signal-icon">{num}</span>
                    <span>{text}</span>
                  </div>
                ))}
              </div>

              <div className="cv7-contact-details">
                {CONTACT_DETAILS.map(({ label, value }) => (
                  <div key={label} className="cv7-contact-detail">
                    <span className="cv7-contact-detail-label">{label}</span>
                    <span className="cv7-contact-detail-value">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="cv7-story-footer">
              Where merit is recognized. Fairness is expected. Opportunity finds its audience.
            </div>
          </aside>

          {/* ── Form Panel ──────────────────────────────────────────────── */}
          <section className="cv7-form-panel">
            <h2 className="cv7-form-title">How can we help?</h2>
            <p className="cv7-form-subtitle">Choose the Opportunity Post area connected to your request.</p>

            <form onSubmit={handleSubmit} data-testid="contact-form">

              {/* Inquiry chips */}
              <div className="cv7-field">
                <div className="cv7-field-label">What do you need help with?</div>
                <div className="cv7-field-note">Choose one primary area.</div>
                <div className="cv7-chips" id="cv7-inquiry-chips" role="radiogroup" aria-label="Primary inquiry area">
                  {INQUIRY_CHIPS.map(chip => (
                    <label
                      key={chip}
                      className={`cv7-chip${selectedInquiry === chip ? ' active' : ''}`}
                      data-testid={`inquiry-chip-${chip.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                    >
                      <input
                        type="radio"
                        name="inquiry"
                        value={chip}
                        required
                        checked={selectedInquiry === chip}
                        onChange={() => setSelectedInquiry(chip)}
                      />
                      {chip}
                    </label>
                  ))}
                </div>
              </div>

              {/* Name */}
              <div className="cv7-grid-2">
                <div className="cv7-field">
                  <label className="cv7-field-label" htmlFor="cv7-firstName">First Name</label>
                  <input
                    id="cv7-firstName" name="firstName" autoComplete="given-name"
                    placeholder="First name" required
                    value={firstName} onChange={e => setFirstName(e.target.value)}
                    data-testid="contact-first-name"
                  />
                </div>
                <div className="cv7-field">
                  <label className="cv7-field-label" htmlFor="cv7-lastName">Last Name</label>
                  <input
                    id="cv7-lastName" name="lastName" autoComplete="family-name"
                    placeholder="Last name" required
                    value={lastName} onChange={e => setLastName(e.target.value)}
                    data-testid="contact-last-name"
                  />
                </div>
              </div>

              {/* Email + Phone */}
              <div className="cv7-grid-2">
                <div className="cv7-field">
                  <label className="cv7-field-label" htmlFor="cv7-email">Email Address</label>
                  <input
                    id="cv7-email" name="email" type="email" autoComplete="email"
                    placeholder="you@example.com" required
                    value={email} onChange={e => setEmail(e.target.value)}
                    data-testid="contact-email"
                  />
                </div>
                <div className="cv7-field">
                  <label className="cv7-field-label" htmlFor="cv7-phone">
                    Phone Number <span className="cv7-opt">(optional)</span>
                  </label>
                  <input
                    id="cv7-phone" name="phone" type="tel" autoComplete="tel"
                    placeholder="+1 (555) 123-4567"
                    value={phone} onChange={e => setPhone(e.target.value)}
                    data-testid="contact-phone"
                  />
                </div>
              </div>

              {/* Organization + Identity */}
              <div className="cv7-grid-2">
                <div className="cv7-field">
                  <label className="cv7-field-label" htmlFor="cv7-organization">
                    Company / Organization <span className="cv7-opt">(optional)</span>
                  </label>
                  <input
                    id="cv7-organization" name="organization" autoComplete="organization"
                    placeholder="Organization name"
                    value={organization} onChange={e => setOrg(e.target.value)}
                    data-testid="contact-organization"
                  />
                </div>
                <div className="cv7-field">
                  <label className="cv7-field-label" htmlFor="cv7-identity">Which best describes you?</label>
                  <select
                    id="cv7-identity" name="identity" required
                    value={identity} onChange={e => setIdentity(e.target.value)}
                    data-testid="contact-identity"
                  >
                    {IDENTITY_OPTIONS.map(o => (
                      <option key={o} value={o}>{o === '' ? 'Select one' : o}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Media toggle */}
              <div className="cv7-toggle-row">
                <div className="cv7-toggle-copy">
                  <strong>Are you contacting us as a member of the media or press?</strong>
                  <span>Turn this on for interviews, comments, statements, speaking requests, or press coverage.</span>
                </div>
                <label className="cv7-switch">
                  <input
                    type="checkbox" name="isMedia"
                    checked={isMedia} onChange={e => setIsMedia(e.target.checked)}
                    data-testid="contact-media-toggle"
                  />
                  <span className="cv7-switch-track"></span>
                </label>
              </div>

              {isMedia && (
                <div className="cv7-conditional" data-testid="contact-media-fields">
                  <label className="cv7-field-label" htmlFor="cv7-mediaType">Type of media inquiry</label>
                  <select
                    id="cv7-mediaType" name="mediaType"
                    value={mediaType} onChange={e => setMediaType(e.target.value)}
                  >
                    {MEDIA_TYPE_OPTIONS.map(o => (
                      <option key={o} value={o}>{o === '' ? 'Select one' : o}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Advertising toggle */}
              <div className="cv7-toggle-row">
                <div className="cv7-toggle-copy">
                  <strong>Are you interested in advertising with Opportunity Post?</strong>
                  <span>Turn this on to select every format, channel, or placement that may fit your campaign.</span>
                </div>
                <label className="cv7-switch">
                  <input
                    type="checkbox" name="advertisingInterest"
                    checked={isAdv} onChange={e => setIsAdv(e.target.checked)}
                    data-testid="contact-advertising-toggle"
                  />
                  <span className="cv7-switch-track"></span>
                </label>
              </div>

              {isAdv && (
                <div className="cv7-conditional" data-testid="contact-advertising-fields">
                  <div className="cv7-field-label">Advertising interests</div>
                  <div className="cv7-field-note">Select all that apply.</div>
                  <div className="cv7-chips" role="group" aria-label="Advertising interests">
                    {AD_INTERESTS.map(interest => (
                      <label
                        key={interest}
                        className={`cv7-chip${adInterests.includes(interest) ? ' active' : ''}`}
                        data-testid={`ad-interest-${interest.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                      >
                        <input
                          type="checkbox" name="adInterests" value={interest}
                          checked={adInterests.includes(interest)}
                          onChange={() => toggleAdInterest(interest)}
                        />
                        {interest}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Message */}
              <div className="cv7-field">
                <label className="cv7-field-label" htmlFor="cv7-message">What would you like to discuss?</label>
                <textarea
                  id="cv7-message" name="message"
                  placeholder="Tell us what you need, what prompted the inquiry, and any timing or context we should know."
                  required
                  value={message} onChange={e => setMessage(e.target.value)}
                  data-testid="contact-message"
                />
              </div>

              {/* Attachment */}
              <div className="cv7-field">
                <label className="cv7-field-label" htmlFor="cv7-attachment">
                  Attach a supporting document <span className="cv7-opt">(optional)</span>
                </label>
                <input
                  id="cv7-attachment" name="attachment" type="file"
                  data-testid="contact-attachment"
                />
              </div>

              <button
                className="cv7-submit" type="submit" disabled={loading}
                data-testid="contact-submit"
              >
                {loading ? 'Sending…' : 'Send Inquiry'}
              </button>

              <label className="cv7-consent">
                <input
                  type="checkbox" name="newsletterOptIn"
                  checked={optIn} onChange={e => setOptIn(e.target.checked)}
                  data-testid="contact-newsletter-opt-in"
                />
                <span>Keep me informed — join our weekly newsletter, "Opportunity Unlocked."</span>
              </label>

              {success && (
                <div className="cv7-success" role="status" data-testid="contact-success">
                  Your inquiry has been received. We'll be in touch shortly.
                </div>
              )}

            </form>
          </section>

        </section>
      </main>
    </div>
  );
}
